import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../Authentication/components/hooks/useAuth';
import useUserOrg from '../Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '../Authentication/components/hooks/useAuthenticatedApi';
import { ExperienceState } from './types';

export default function OrganizationGalleryLink() {
    const { auth } = useAuth();
    const { orgId } = useUserOrg();
    const api = useAuthenticatedApi();
    const canEdit = !!auth?.roles?.some(
        (role: number) => role === 0 || role === 69,
    );
    const [result, setResult] = useState<{
        orgId: number;
        state?: ExperienceState;
        error?: string;
    } | null>(null);
    const [saving, setSaving] = useState(false);
    const [reload, setReload] = useState(0);
    const currentOrg = useRef(orgId);
    currentOrg.current = orgId;
    const base = `/gallery-experiences/organizations/${orgId}`;
    useEffect(() => {
        if (!canEdit || !orgId) return;
        let active = true;
        setResult(null);
        setSaving(false);
        api.get(base)
            .then(({ data }) => {
                if (active) setResult({ orgId, state: data });
            })
            .catch(() => {
                if (active)
                    setResult({
                        orgId,
                        error: 'Unable to load gallery defaults. Please try again.',
                    });
            });
        return () => {
            active = false;
        };
    }, [api, base, orgId, canEdit, reload]);
    if (!orgId || !canEdit) return null;
    const state = result?.orgId === orgId ? result.state : undefined;
    const error = result?.orgId === orgId ? result.error : undefined;
    return (
        <section className="my-4 rounded-lg border border-gray-200 bg-white p-4 text-gray-900">
            <h2 className="font-semibold">Gallery defaults</h2>
            <p className="my-2 text-sm text-gray-600">
                Choose a reusable template or design a starting gallery for this
                organization.
            </p>
            <label className="my-3 flex items-center gap-2 text-sm font-semibold">
                <input
                    type="checkbox"
                    checked={state?.enabled || false}
                    disabled={saving || !state?.publishedRevision || !!error}
                    onChange={async (event) => {
                        if (!state || saving) return;
                        const enabled = event.target.checked;
                        setSaving(true);
                        try {
                            const { data } = await api.put(base + '/enabled', {
                                version: state.version,
                                enabled,
                            });
                            if (currentOrg.current === orgId)
                                setResult({ orgId, state: data });
                        } catch (err: any) {
                            if (currentOrg.current === orgId)
                                setResult({
                                    orgId,
                                    state,
                                    error:
                                        err?.response?.data?.message ||
                                        'Unable to update the gallery setting. Reload and try again.',
                                });
                        } finally {
                            if (currentOrg.current === orgId) setSaving(false);
                        }
                    }}
                />
                Use custom galleries for new activations
            </label>
            {!state && !error && (
                <p role="status" className="my-2 text-sm">
                    Loading gallery defaults…
                </p>
            )}
            {state && (
                <p className="my-2 text-sm text-gray-600">
                    {!state.publishedRevision
                        ? 'Publish a default design or choose a template as the future default before enabling this setting.'
                        : state.enabled
                          ? 'New activations start with their own live copy of the published default.'
                          : 'New activations use Classic and receive an editable draft of the published default.'}{' '}
                    Existing galleries keep their current settings.
                </p>
            )}
            {error && (
                <div className="my-2 text-sm">
                    <p role="alert">{error}</p>
                    <button
                        type="button"
                        className="underline"
                        onClick={() => setReload((value) => value + 1)}
                    >
                        Reload gallery setting
                    </button>
                </div>
            )}
            <Link
                className="inline-block rounded-md bg-black px-4 py-2 text-sm font-semibold text-white"
                to={`/gallery-experiences/organization-defaults/${orgId}`}
            >
                Edit gallery defaults
            </Link>
        </section>
    );
}
